/* eslint-disable @typescript-eslint/no-invalid-void-type */
/**
 * Extension pairing + status hooks.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApiClient } from '../context';
import type { ApiError } from '../hc';
import { getJson, postJson } from './_http';

// TODO Wave 3 — Extension contracts.
export interface PairExtensionResponse {
  code: string;
  expiresIn: number;
}

export interface ExtensionStatusAccount {
  id: string;
  name?: string | null;
  initials?: string | null;
  avatarUrl?: string | null;
  profileUrl?: string | null;
  status?: 'active' | 'paused' | 'error' | 'limited' | string;
}

export interface ExtensionStatusLink {
  id: string;
  extVersion?: string | null;
  lastSeenAt?: string | number | null;
  createdAt?: string | number | null;
  revoked?: boolean;
}

export interface ExtensionStatusResponse {
  paired: boolean;
  accountConnected: boolean;
  extension: ExtensionStatusLink | null;
  account: ExtensionStatusAccount | null;
}

/** `POST /api/v1/ext/pair` — mint a one-time extension pairing code. */
export function usePairExtension(): UseMutationResult<PairExtensionResponse, ApiError, void> {
  const client = useApiClient();
  return useMutation<PairExtensionResponse, ApiError, void>({
    mutationFn: () => postJson<PairExtensionResponse>(client, '/api/v1/ext/pair', {}),
  });
}

export interface DisconnectExtensionResponse {
  ok: boolean;
  revokedLinks: number;
  archivedAccounts: number;
}

/**
 * `POST /api/v1/ext/disconnect` — full reset of the user's extension pairings
 * and connected LinkedIn accounts. Revokes every `extensionLinks` row and
 * archives every `linkedinAccounts` row. Idempotent.
 */
export function useDisconnectExtension(): UseMutationResult<
  DisconnectExtensionResponse,
  ApiError,
  void
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<DisconnectExtensionResponse, ApiError, void>({
    mutationFn: () => postJson<DisconnectExtensionResponse>(client, '/api/v1/ext/disconnect', {}),
    onSuccess: async () => {
      // Cancel any old status request, then clear its connected snapshot
      // before the Connect screen switches back to the pairing panel.
      // Otherwise that panel can consume stale account data, immediately call
      // `onConnected`, and hide the freshly generated pairing code again.
      await queryClient.cancelQueries({ queryKey: ['extension-status'] });
      queryClient.setQueryData<ExtensionStatusResponse>(['extension-status'], {
        paired: false,
        accountConnected: false,
        extension: null,
        account: null,
      });
      await queryClient.invalidateQueries({ queryKey: ['extension-status'] });
    },
  });
}

/** `GET /api/v1/ext/status` — web-safe extension pairing/account status. */
export function useExtensionStatus(
  options: { enabled?: boolean; refetchInterval?: number | false; retry?: number | boolean } = {},
): UseQueryResult<ExtensionStatusResponse, ApiError> {
  const client = useApiClient();
  return useQuery<ExtensionStatusResponse, ApiError>({
    queryKey: ['extension-status'],
    queryFn: () => getJson<ExtensionStatusResponse>(client, '/api/v1/ext/status'),
    enabled: options.enabled,
    refetchInterval: options.refetchInterval,
    retry: options.retry ?? false,
  });
}
